
import React, { useState, useEffect } from "react";
import { useOutletContext, Link } from "react-router-dom";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

type Proposal = {
  id: string;
  title: string;
  status: string;
};

const AllProposals = () => {
  // Render the title in the outlet context
  if (useOutletContext() === "title-only") {
    return "All Proposals";
  }
  
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const { toast } = useToast();
  
  // Load proposals from localStorage or initialize with sample data
  useEffect(() => {
    const loadProposals = () => {
      // Sample data for demonstration
      const sampleData = [
        {
          id: "pro-1",
          title: "Corporate Litigation Review",
          status: "Draft",
        },
        {
          id: "pro-2",
          title: "IP Protection Strategy",
          status: "Complete",
        },
        {
          id: "pro-3",
          title: "Employment Law Compliance",
          status: "Active",
        },
        {
          id: "pro-4",
          title: "Contract Negotiation",
          status: "Draft",
        },
        {
          id: "pro-5",
          title: "Mergers and Acquisitions",
          status: "Complete",
        }
      ];
      
      // Try to load from localStorage first
      const savedProposals = localStorage.getItem("all-proposals");
      if (savedProposals) {
        try {
          const parsedProposals = JSON.parse(savedProposals);
          setProposals(parsedProposals);
        } catch (error) {
          console.error("Error parsing proposals:", error);
          toast({
            title: "Error loading proposals",
            description: "There was a problem loading your proposals. Loading sample data instead.",
            variant: "destructive"
          });
          // Set sample data if parsing fails
          setProposals(sampleData);
          localStorage.setItem("all-proposals", JSON.stringify(sampleData));
        }
      } else {
        // If no data in localStorage, set sample data
        setProposals(sampleData);
        localStorage.setItem("all-proposals", JSON.stringify(sampleData));
      }
    };
    
    loadProposals();
    
    // Add event listener for storage changes to refresh data
    window.addEventListener("storage", (e) => {
      if (e.key === "all-proposals") {
        loadProposals();
      }
    });
    
    return () => {
      window.removeEventListener("storage", () => {});
    };
  }, [toast]);
  
  return (
    <div className="animate-fade-in">
      <div className="bg-white shadow rounded-lg p-6 border border-gray-200">
        <h2 className="text-2xl font-semibold mb-4">All Proposals</h2>
        <p className="text-gray-600 mb-6">
          View and manage all your proposals and templates here.
        </p>
        
        {proposals.length === 0 ? (
          <div className="p-8 border border-dashed border-gray-300 rounded-lg text-center">
            <p className="text-gray-500">All your proposals and templates will appear here</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name of Matter</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>View Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {proposals.map((proposal) => (
                    <TableRow key={proposal.id}>
                      <TableCell className="font-medium">
                        {proposal.title || "Untitled Proposal"}
                      </TableCell>
                      <TableCell>
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100">
                          {proposal.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="outline" 
                          size="sm"
                          disabled
                          className="cursor-not-allowed"
                          title="demo"
                        >
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllProposals;
